import { type Column } from "@tanstack/react-table";
import { cn } from "@/lib/utils";

import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronsUpDown,
} from "lucide-react";
interface DataTableColumnHeaderProps<TData, TValue>
  extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
  sortKey: string; // Key to identify the column for sorting
  toggleSortOrder: (key: string, currentOrder: string) => void; // Function to toggle sort order
  searchCriteria: { sort_key: string; sort_order: string }; // Current search criteria
  sortable?: boolean;
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
  sortKey,
  toggleSortOrder,
  searchCriteria,
  sortable = true,
}: DataTableColumnHeaderProps<TData, TValue>) {
  const isSorted =
    searchCriteria.sort_key && searchCriteria?.sort_key === sortKey;
  const isAsc = isSorted && searchCriteria.sort_order === "asc";
  const isDesc = isSorted && searchCriteria.sort_order === "desc";

  const handleToggleSort = () => {
    if (!sortable) return; // Skip toggling for non-sortable columns

    let nextSortOrder: string;
    if (isAsc) {
      nextSortOrder = "desc";
    } else if (isDesc) {
      nextSortOrder = ""; // Clear sorting
    } else {
      nextSortOrder = "asc";
    }
    toggleSortOrder(sortKey, nextSortOrder);
  };

  return (
    <div
      className={cn(
        "font-poppins flex items-center space-x-2",
        { "cursor-pointer": sortable }, // Visual hint for non-sortable columns
        className
      )}
      onClick={handleToggleSort}
      aria-label={
        sortable
          ? isDesc
            ? `Sorted descending. Click to clear sorting.`
            : isAsc
              ? `Sorted ascending. Click to sort descending.`
              : `Not sorted. Click to sort ascending.`
          : `Not sortable`
      }
    >
      <span className={"text-nowrap"}>{title}</span>
      {sortable &&
        (isDesc ? (
          <ArrowDownIcon className="ml-2 h-4 w-4" aria-hidden="true" />
        ) : isAsc ? (
          <ArrowUpIcon className="ml-2 h-4 w-4" aria-hidden="true" />
        ) : (
          <ChevronsUpDown className="ml-2 h-4 w-4" aria-hidden="true" />
        ))}
    </div>
  );
}
